import axios from 'axios'
import {
  GA4EcommData,
  GA4PurchaseEcommData,
  GA4ShadowCart,
  GA4Error,
  GA4CaptureErrorEventData,
  GA4ProductItem,
  StoreData,
  GA4TransactionEventData,
  TransactionData,
  GA4EventData,
  GA4PaymentEcommData,
  GA4EventMediaData,
  GA4EventTaskData,
} from '../classes/ga4-classes'
import { GA4Constants } from '../constants/analytics-constants'
import { GA4DimensionConfig } from '../interfaces/analytics-interfaces'
import { AxWindow } from '@aspenware-library/types'

/**
 * Handles any error that is specific to the GA4 code
 * @param {string} eventName GA4 event name
 * @param {string} errorMessage Error message
 * @param {string} method method name where error occurred
 * @returns {Boolean} alway true so processing isn't interrupted
 */
export function handleGA4Error(eventName: string, errorMessage: string, method: string) {
  const ga4error = new GA4Error(eventName, errorMessage, method)

  console.error(`GA4 ERROR \n event: ${ga4error.event} method: ${ga4error.method} message: ${ga4error.message}`)

  return true
}

/**
 * Adds up the prices of an array of GA4 items
 * @param {GA4ProductItem[]} items Array of product items
 * @returns {number} total value of the items
 */
export function sumGA4EventValue(items: GA4ProductItem[]) {
  let eCommValue: number = 0
  items?.forEach((item) => {
    const price = item.price || 0
    eCommValue += item?.quantity ? price * item.quantity : price
  })

  return Number(eCommValue.toFixed(2))
}

/**
 * Calculates the discount of a product item based on the price with and without discount
 * @param {any} productItem Product data in common AXCommerce format
 * @returns {number} discount value
 */
export function calculateGA4Discount(productItem: any) {
  if (productItem.ProductPrice?.PriceValue && productItem.ProductPrice?.PriceValueWithDiscount) {
    return productItem.ProductPrice.PriceValue - productItem.ProductPrice.PriceValueWithDiscount
  }

  return 0
}

/**
 * Build a variant name for a given product to be used in GA4 events
 * @param {any} productItem Product data in common AXCommerce format
 * @returns {string} variant name
 */
export function buildVariantName(productItem: any) {
  const selectedAttributeNames = productItem.ProductAttributes.map((attribute) => {
    if (!attribute.Values || !attribute.IsRequired || !attribute.SelectedValueId) {
      return undefined
    }
    const selectedAttribute = attribute.Values.find((value) => {
      return value.Id == +attribute.SelectedValueId
    })
    // Some attributes are pipe-delimited so we only want the first portion of the name
    return selectedAttribute?.Name.split('|')[0].trim() || undefined
  }).filter((name: string | undefined) => !!name)

  return [productItem.Name, ...selectedAttributeNames].join(' - ')
}

/**
 * Retrieves a specific cart item from local storage
 * @param {string} cartId ID of the current user's cart (e.g. cart_123456)
 * @param {number} cartItemId Option ID of a specific cart item to retrieve
 * @returns {Array} Array of cart items
 */
export function getLocalStorageCartItem(cartItemId: number = 0): GA4ProductItem[] {
  const cart = JSON.parse(localStorage.getItem(GA4Constants.LOCAL_CART_ID) || '{}')
  const localStorageCartItem = cart[cartItemId]
  if (!localStorageCartItem) return []
  return localStorageCartItem
}

/**
 * Retrieves the entire shadow cart from local storage
 * @returns {GA4ShadowCart} containing all local cart items
 */
export function getLocalStorageCart(): GA4ShadowCart {
  return JSON.parse(localStorage.getItem(GA4Constants.LOCAL_CART_ID) || '{}')
}

/**
 * Removes cart items from local storage
 * @param {string} cartId ID of the current user's cart (e.g. cart_123456)
 * @param {number} cartItemId Option ID of a specific cart item to remove
 */
export function removeLocalStorageItems(cartId: string, cartItemId: number = 0) {
  if (cartId) {
    const localCart = getLocalStorageCart()
    // If a cart item ID was passed, just remove that item
    if (cartItemId > 0) {
      delete localCart[cartItemId]
      localStorage.setItem(cartId, JSON.stringify(localCart))
    } else {
      // If no cart item ID was passed, remove all cart items from the cart
      localStorage.removeItem(cartId)
    }
  } else {
    // If no cart ID was passed, just pass a blank array
    console.log('ERROR:  removeLocalStorageItems() called but no cart ID passed')
  }
}

/**
 * @param {StoreData} storeData Data relating to the eCommerce store
 * @param {String} eventName Name of the GA4 event
 * @param {Object} transactionData ID of the eCommerce transaction
 */
export function generateGA4TransactionEvent(storeData: StoreData, eventName: string, transactionData: TransactionData) {
  try {
    const GA4TxnEventData: GA4TransactionEventData = new GA4TransactionEventData(eventName, storeData, transactionData)

    // First send an event to clear ecommerce
    pushToDataLayer({ event: GA4Constants.EVENTS.CLEAR_ECOMMERCE, ecommerce: null })

    // Send the GA4 event
    pushToDataLayer(GA4TxnEventData)
  } catch (error: any) {
    handleGA4Error(eventName, error, 'generateGA4TransactionEvent')
  }
}
/**
 * Builds an array of cart items for use in Google Analytics by marrying Nop Cart item data and cart item
 * data in local storage
 * @param {Array} nopCartItems Cart items from backend NOP cart
 * @param {Array} localStorageCartItems Additional cart item data from local storage (optional)
 */
export async function buildGA4CartItems(
  nopCartItems: Array<any> | null,
  localStorageCartItems: Array<GA4ProductItem> | null
): Promise<GA4ProductItem[]> {
  if (!nopCartItems) return []

  const GA4CartItems: GA4ProductItem[] = []

  // The array is based off of NOP cart items
  for (let itemIndex = 0; itemIndex < nopCartItems.length; itemIndex++) {
    const nopCartItem = nopCartItems[itemIndex]

    // Look for matching local storage data
    const localStorageCartItemIndex =
      localStorageCartItems?.findIndex((item) => item.cart_item_id == nopCartItem.Id) ?? -1
    const localStorageCartItem =
      localStorageCartItemIndex >= 0 && localStorageCartItems ? localStorageCartItems[localStorageCartItemIndex] : null

    const { item_category, item_category2, item_category3, item_list_id, item_list_name, item_variant } =
      await getCartItemDetailsForEvent(nopCartItem, localStorageCartItem)

    // this gets the add-on items for the current nop cart item
    const addOnItems: GA4ProductItem[] = []
    if (localStorageCartItems) {
      for (let i = localStorageCartItemIndex + 1; i < localStorageCartItems.length; i++) {
        const item = localStorageCartItems[i]
        if (item.item_list_id === GA4Constants.ITEM_LISTS.ADD_ONS.ID) {
          addOnItems.push(item)
        } else {
          break
        }
      }
    }

    // Create a GA4 cart based on NOP Cart item data and local storage item data
    const GA4CartItem = new GA4ProductItem({
      item_id: localStorageCartItem?.item_id!,
      cart_item_id: nopCartItem.Id,
      item_list_id,
      item_list_name,
      item_name: localStorageCartItem?.item_name || nopCartItem.ProductName,
      discount: nopCartItem.DiscountInfo?.Amount || 0,
      coupon: null,
      item_category,
      item_category2,
      item_category3,
      item_variant,
      item_brand: null,
      price: localStorageCartItem!.price,
      location_id: null,
      sku: localStorageCartItem!.sku,
      affiliation: window?.storeSettings?.Store?.Name || GA4Constants.DEFAULT_AFFILIATION,
      quantity: localStorageCartItem!.quantity! || 1,
      index: GA4CartItems.length,
    })

    const ga4ddOnItems = addOnItems.map((item, index) => {
      return {
        ...item,
        item_category,
        item_category2,
        item_category3,
        index: GA4CartItems.length + index + 1,
      }
    })

    GA4CartItems.push(GA4CartItem, ...ga4ddOnItems)
  }
  return GA4CartItems
}

/**
 * Attempts to get details for cart items from localStorage if available,
 * otherwise fetches the details from the server
 * @param nopCartItem Cart items from backend NOP Cart
 * @param localStorageCartItem Cart item data from local storage (optional)
 * @returns An object containing cart item category, list, and variant details
 */
export async function getCartItemDetailsForEvent(nopCartItem: any, localStorageCartItem?: GA4ProductItem | null) {
  let item_category = GA4Constants.DEFAULT_CATEGORY
  let item_category2 = null as string | null
  let item_category3 = null as string | null

  if (localStorageCartItem) {
    // Use the local storage item's category data if it exists
    item_category = localStorageCartItem.item_category || GA4Constants.DEFAULT_CATEGORY
    item_category2 = localStorageCartItem.item_category2 || null
  } else {
    item_category = nopCartItem.ProductCategoryPrimaryName
    item_category2 = nopCartItem.ProductCategorySubName
    if (!item_category) {
      // Try to fetch the product details from the server
      try {
        const productDetails = (await axios.get(`/api/productdetails/${nopCartItem.ProductId}`)).data
        const categoryBreadcrumbs = productDetails?.Breadcrumb?.CategoryBreadcrumb ?? []
        const [category, category2, category3] = categoryBreadcrumbs
        item_category = category?.Name ?? GA4Constants.DEFAULT_CATEGORY
        item_category2 = category2?.Name ?? null
        item_category3 = category3?.Name ?? null
      } catch (error: any) {
        handleGA4Error('getCartItemCategories', error, 'axios.get')
      }
    }
  }
  return {
    item_category,
    item_category2,
    item_category3,
    item_list_id: nopCartItem.ProductItemListId || localStorageCartItem?.item_list_id || GA4Constants.DEFAULT_LIST_ID,
    item_list_name:
      nopCartItem.ProductItemListName || localStorageCartItem?.item_list_name || GA4Constants.DEFAULT_LIST_NAME,
    item_variant: nopCartItem.ProductVariantPricing[0].VariantName || localStorageCartItem?.item_variant,
  }
}

/**
 * Maps the payment type to a GA4 friendly name
 * Mapping is case and space insensitive
 * @param {string} paymentType
 * @returns {string} GA4 friendly payment type or undefined if not found
 */
export function mapPaymentTypeToGA4(paymentType?: string): string | undefined {
  if (!paymentType) {
    return undefined
  }
  const paymentTypeMap: { [key: string]: string } = {
    creditcard: 'Credit Card',
    applepay: 'Apple Pay',
    googlepay: 'Google Pay',
    paypal: 'PayPal',
    storedvalue: 'Stored Value',
    paymentvaluevoucher: 'Payment Value Voucher',
    rewardpoints: 'Reward Points',
    giftcard: 'Gift Card',
  }

  const searchTerm = paymentType.toLowerCase().replace(/\s+/g, '')
  return paymentTypeMap[searchTerm]
}

/**
 * generateGA4Event()
 *
 * @param {GA4DimensionConfig} config A GA4 dimensions configuration object containing all the data needed to generate a GA4 event
 */
export async function generateGA4Event(config: GA4DimensionConfig) {
  const {
    nopCartItems,
    storeData,
    eventName,
    localCartId,
    transactionId,
    userEmail,
    userPhone,
    tax,
    shipping,
    paymentType,
    tasks,
    mediaCardCount,
  } = config

  let localStorageCartItems = config.localStorageCartItems

  try {
    //First get the local storage cart items
    if (!localStorageCartItems || localStorageCartItems.length <= 0) {
      localStorageCartItems = Object.values(getLocalStorageCart()).flatMap((items) => {
        return items
      })
    }

    let GA4CartItems: GA4ProductItem[]

    // If NOP cart items were passed, build GA4 items based off of those
    if (nopCartItems) {
      GA4CartItems = nopCartItems.length ? await buildGA4CartItems(nopCartItems, localStorageCartItems) : []
    } else {
      // If no NOP cart items were passed, we have to rely solely on the localStorage.
      // This is because events created in the checkout process have no access to the cart.
      GA4CartItems = localStorageCartItems ? localStorageCartItems : []
    }

    // Set ecommerce data
    let eCommData: GA4EcommData
    let GA4PaymentType: string | undefined = undefined
    if (paymentType) {
      GA4PaymentType = mapPaymentTypeToGA4(paymentType)
    }

    if (eventName == GA4Constants.EVENTS.PURCHASE) {
      eCommData = new GA4PurchaseEcommData(
        storeData,
        GA4CartItems,
        transactionId,
        userEmail,
        userPhone,
        tax || 0,
        shipping || 0,
        GA4PaymentType
      )
    } else if (
      eventName == GA4Constants.EVENTS.ADD_PAYMENT_SELECTION ||
      eventName == GA4Constants.EVENTS.CONFIRM_PAYMENT
    ) {
      eCommData = new GA4PaymentEcommData(storeData, GA4CartItems, GA4PaymentType)
    } else {
      eCommData = new GA4EcommData(storeData, GA4CartItems)
    }

    // Set event data
    let eventData: GA4EventData
    if (eventName == GA4Constants.EVENTS.ADD_PERSONALIZATION) {
      eventData = new GA4EventTaskData(eventName, eCommData, tasks || [])
    } else if (eventName == GA4Constants.EVENTS.ADD_MEDIA) {
      eventData = new GA4EventMediaData(eventName, eCommData, mediaCardCount || 0)
    } else {
      eventData = new GA4EventData(eventName, eCommData)
    }

    pushToDataLayer({
      event: GA4Constants.EVENTS.CLEAR_ECOMMERCE,
      ecommerce: null,
      media_card_count: null,
    })
    pushToDataLayer(eventData)
  } catch (error: any) {
    handleGA4Error(eventName, error, 'generateGA4Event')
  }
}

/**
 * Creates a data object for the error event and pushes it to the data layer
 * @param {string} eventName Name of the GA4 event - eg. 'error'
 * @param {string} errorType Type of error - eg. 'checkout personalization error'
 * @param {string} errorMessage Error message
 */
export function generateGA4ErrorEvent(eventName: string, errorType: string, errorMessage: string) {
  try {
    const GA4ErrorEventData = new GA4CaptureErrorEventData(eventName, errorType, errorMessage)
    pushToDataLayer(GA4ErrorEventData)
  } catch (error: any) {
    handleGA4Error('GA4 Error', error, 'generateGA4ErrorEvent')
  }
}

/**
 * Determines the ItemListId and ItemListName to be used for the event
 * @param routeName Name of the current route - used to determine if Fenced Voucher Product or not
 * @param checkTempCartData Whether to use the tempCartData or not
 * @returns Object with listId and listName
 */
export function determineItemListInfo(routeName: string, checkTempCartData: boolean = true): { listId: string, listName: string } {
  let listId = ''
  let listName = ''
  let itemListData: { id: string, name: string } | null = null

  if (checkTempCartData) {
    const tempCartData = sessionStorage.getItem('tempCartData')
    itemListData = tempCartData ? tempCartData as unknown as { id: string, name: string } : null
  }

  if (routeName === 'voucher-product') {
    listId = GA4Constants.ITEM_LISTS.FENCED_VOUCHER.ID
    listName = GA4Constants.ITEM_LISTS.FENCED_VOUCHER.NAME
  }
  else {
    // assume deep link if no temp cart data exists
    listId = itemListData?.id ?? GA4Constants.ITEM_LISTS.DIRECT.ID
    listName = itemListData?.name ?? GA4Constants.ITEM_LISTS.DIRECT.NAME
  }

  return { listId, listName }
}

/**
 * Parses data into a JSON object and pushes the data to the aspenware Data Layer
 * @param data Analytics Data to send to the data layer
 */
export function pushToDataLayer(data: { event: string, [key: string]: any }) {
  try {
    const win = window as unknown as AxWindow

    // stringify and parse the data into JSON Object
    const jsonData = JSON.parse(JSON.stringify(data))

    // Push to the data layer
    win.aspenwareDataLayer.push(jsonData)
  } catch (error: any) {
    throw error
  }
}
